import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';
import EnterNavForm from '../../components/EnterNavForm';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const BirlesikMalKabulPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_GRN);

  // Tab state
  const [activeTab, setActiveTab] = useState('yeni');

  // === TAB 1: Yeni Mal Kabul ===
  const [mode, setMode] = useState('siparis'); // 'siparis' veya 'dogrudan'
  const [siparisler, setSiparisler] = useState([]);
  const [selectedSiparisId, setSelectedSiparisId] = useState('');
  const [tedarikciler, setTedarikciler] = useState([]);
  const [selectedTedarikciId, setSelectedTedarikciId] = useState('');
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [selectedLokasyonId, setSelectedLokasyonId] = useState('1');
  const [teslimatNotu, setTeslimatNotu] = useState('');
  const [kalemler, setKalemler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  // Malzeme bazlı BirimDonusum cache (malzeme_id → BD array). Doğrudan mal kabulde
  // kullanıcı alış birimini seçer; payload'da giris_birimi gider, backend stok
  // birimine çevirir (sipariş modunda backend siparis_kalem.birim'i kullanır).
  const [bdCache, setBdCache] = useState({});

  // Kalite Kontrol
  const [renkSkoru, setRenkSkoru] = useState(5);
  const [kokuSkoru, setKokuSkoru] = useState(5);
  const [dokuSkoru, setDokuSkoru] = useState(5);
  const [sicaklikC, setSicaklikC] = useState('');
  const [sicaklikOk, setSicaklikOk] = useState(true);
  const [yagOraniOk, setYagOraniOk] = useState(true);
  const [ambalajOk, setAmbalajOk] = useState(true);
  const [kaliteNot, setKaliteNot] = useState('');

  // Sonuç
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // === TAB 2: Geçmiş Kayıtlar ===
  const [gecmisGrnler, setGecmisGrnler] = useState([]);
  const [gecmisLoading, setGecmisLoading] = useState(false);
  const [gecmisFiltre, setGecmisFiltre] = useState('');
  const [expandedGrn, setExpandedGrn] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);

  // Başlangıç verileri yükle
  useEffect(() => {
    loadSiparisler();
    loadTedarikciler();
    loadLokasyonlar();
    loadMalzemeler();
  }, []);

  useEffect(() => {
    if (activeTab === 'gecmis') loadGecmisGrnler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadSiparisler = async () => {
    try {
      const res = await fetch(`${API_URL}/api/siparisler`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.items || []);
      setSiparisler(arr.filter(s =>
        ['YOLDA', 'SIPARIS_VERILDI', 'ONAYLANDI', 'KISMI_TESLIM'].includes(s.durum)
      ));
    } catch (e) { logError('Sipariş yükleme hatası:', e); }
  };

  const loadTedarikciler = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      setTedarikciler(await res.json());
    } catch (e) { logError('Tedarikçi yükleme hatası:', e); }
  };

  const loadLokasyonlar = async () => {
    try {
      const res = await fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setLokasyonlar(Array.isArray(data) ? data : []);
    } catch (e) { logError('Lokasyon yükleme hatası:', e); }
  };

  const loadMalzemeler = async () => {
    try {
      const res = await fetch(`${API_URL}/api/malzemeler?limit=500`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setMalzemeler(Array.isArray(data) ? data : (data.items || []));
    } catch (e) { logError('Malzeme yükleme hatası:', e); }
  };

  const loadGecmisGrnler = async () => {
    setGecmisLoading(true);
    try {
      const params = new URLSearchParams();
      if (gecmisFiltre) params.append('durum', gecmisFiltre);
      const res = await fetch(`${API_URL}/api/grn?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      setGecmisGrnler(await res.json());
    } catch (e) { logError('GRN yükleme hatası:', e); }
    setGecmisLoading(false);
  };

  const loadGrnDetail = async (grnId) => {
    try {
      const res = await fetch(`${API_URL}/api/grn/${grnId}`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      setExpandedDetail(await res.json());
    } catch (e) { logError('GRN detay hatası:', e); }
  };

  // Sipariş seçildiğinde kalemleri yükle
  const handleSiparisSecimi = async (siparisId) => {
    setSelectedSiparisId(siparisId);
    if (!siparisId) { setKalemler([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/siparisler/${siparisId}`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const items = data.kalemler || [];
      setKalemler(items.map(k => ({
        malzeme_id: k.malzeme_id,
        malzeme_ad: k.malzeme_ad || k.malzeme?.ad || '',
        birim: k.birim || 'kg',
        siparis_miktar: k.miktar || 0,
        teslim_miktar: k.miktar || 0,
        birim_fiyat: k.birim_fiyat || null,
        siparis_kalem_id: k.id,
        durum: 'OK',
        not_kalem: ''
      })));
      // Auto-fill tedarikci
      if (data.tedarikci_id) setSelectedTedarikciId(String(data.tedarikci_id));
    } catch (e) { logError('Sipariş detay hatası:', e); }
  };

  // Doğrudan girişte kalem ekle/sil
  const addDogruKalem = () => {
    setKalemler(prev => [...prev, {
      malzeme_id: '', malzeme_ad: '', birim: 'kg', giris_birimi: '',
      siparis_miktar: 0, teslim_miktar: 0, birim_fiyat: null,
      siparis_kalem_id: null, durum: 'OK', not_kalem: ''
    }]);
  };

  const removeKalem = (idx) => {
    setKalemler(prev => prev.filter((_, i) => i !== idx));
  };

  // Malzemenin BirimDonusum tanımlarını yükle/cache'le. Default giris_birimi BİLİNÇLİ
  // olarak boş ("çevirme yok") bırakılır — geçmişte GRN miktarı stok biriminde
  // giriliyordu; otomatik alış birimi ataması sessiz şişme riski taşır. Kullanıcı
  // alış biriminde teslim aldıysa dropdown'dan açıkça seçer.
  const loadBdForMalzeme = (malzemeId) => {
    if (!malzemeId || bdCache[malzemeId]) return;
    fetch(`${API_URL}/api/birim-donusumleri?malzeme_id=${malzemeId}`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : [])
      .then(bds => {
        if (Array.isArray(bds)) setBdCache(prev => ({ ...prev, [malzemeId]: bds }));
      })
      .catch(() => {});
  };

  // Doğrudan modda kalem için alış birimi seçenekleri (malzemenin aktif BD'leri).
  // BD yoksa boş döner → seçici gösterilmez (miktar stok biriminde varsayılır).
  const getGirisBirimiOpts = (k) => {
    const bds = bdCache[k.malzeme_id];
    if (!Array.isArray(bds) || bds.length === 0) return [];
    const opts = new Set();
    bds.forEach(bd => { if (bd.alis_birimi) opts.add(bd.alis_birimi); });
    return [...opts];
  };

  const updateKalem = (idx, field, value) => {
    setKalemler(prev => prev.map((k, i) => {
      if (i !== idx) return k;
      const updated = { ...k, [field]: value };
      if (field === 'teslim_miktar' || field === 'siparis_miktar') {
        const fark = (updated.teslim_miktar || 0) - (updated.siparis_miktar || 0);
        const farkOran = updated.siparis_miktar > 0 ? (fark / updated.siparis_miktar * 100) : 0;
        updated.durum = Math.abs(farkOran) <= 2 ? 'OK' : (fark < 0 ? 'EKSIK' : 'FAZLA');
      }
      if (field === 'malzeme_id') {
        const m = malzemeler.find(ml => ml.id === Number(value));
        if (m) {
          updated.malzeme_ad = m.ad;
          updated.birim = m.birim || 'kg';
        }
        updated.giris_birimi = '';  // BD yüklenince (aşağıda) default'lanır
      }
      return updated;
    }));
    if (field === 'malzeme_id') loadBdForMalzeme(Number(value));
  };

  // Kalite kontrol sonuç önizleme
  const kaliteSonuc = (!sicaklikOk || Math.min(renkSkoru, kokuSkoru, dokuSkoru) <= 2) ? 'REDDEDILDI' : 'ONAYLANDI';

  // Sıcaklık auto-check
  useEffect(() => {
    if (sicaklikC !== '' && Number(sicaklikC) > 4) {
      setSicaklikOk(false);
    } else if (sicaklikC !== '') {
      setSicaklikOk(true);
    }
  }, [sicaklikC]);

  // Submit
  const handleSubmit = async () => {
    if (kalemler.length === 0) { alert('En az bir kalem ekleyin'); return; }
    if (!selectedLokasyonId) { alert('Lokasyon seçin'); return; }

    const validKalemler = kalemler.filter(k => k.malzeme_id && k.teslim_miktar > 0);
    if (validKalemler.length === 0) { alert('Geçerli kalem yok (malzeme ve miktar girilmeli)'); return; }

    setSubmitting(true);
    try {
      const body = {
        siparis_id: mode === 'siparis' ? Number(selectedSiparisId) || null : null,
        tedarikci_id: Number(selectedTedarikciId) || null,
        lokasyon_id: Number(selectedLokasyonId),
        teslimat_notu: teslimatNotu,
        kalemler: validKalemler.map(k => ({
          malzeme_id: Number(k.malzeme_id),
          siparis_miktar: k.siparis_miktar || k.teslim_miktar,
          teslim_miktar: k.teslim_miktar,
          birim_fiyat: k.birim_fiyat ? Number(k.birim_fiyat) : null,
          siparis_kalem_id: k.siparis_kalem_id || null,
          // Doğrudan modda kullanıcının seçtiği alış birimi (varsa). Sipariş modunda
          // backend siparis_kalem.birim'i kullanır → burada göndermeyiz.
          giris_birimi: mode === 'dogrudan' ? (k.giris_birimi || null) : null,
          not_kalem: k.not_kalem || ''
        })),
        kalite_kontrol: {
          renk_skoru: renkSkoru,
          koku_skoru: kokuSkoru,
          doku_skoru: dokuSkoru,
          sicaklik_c: sicaklikC ? Number(sicaklikC) : null,
          sicaklik_ok: sicaklikOk,
          yag_orani_ok: yagOraniOk,
          ambalaj_ok: ambalajOk,
          aciklama: kaliteNot
        }
      };

      const res = await fetch(`${API_URL}/api/grn/mal-kabul-tamamla`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
    } catch (err) {
      alert(`Hata: ${err.message}`);
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setMode('siparis');
    setSelectedSiparisId('');
    setSelectedTedarikciId('');
    setTeslimatNotu('');
    setKalemler([]);
    setRenkSkoru(5); setKokuSkoru(5); setDokuSkoru(5);
    setSicaklikC(''); setSicaklikOk(true);
    setYagOraniOk(true); setAmbalajOk(true);
    setKaliteNot('');
    setResult(null);
  };

  // Toplam hesapla
  const toplamTeslim = kalemler.reduce((s, k) => s + (Number(k.teslim_miktar) || 0), 0);
  const toplamSiparis = kalemler.reduce((s, k) => s + (Number(k.siparis_miktar) || 0), 0);

  // Skor render
  const SkorInput = ({ label, icon, value, onChange }) => (
    <div style={{ flex: 1, minWidth: '120px' }}>
      <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>{icon} {label}</label>
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => onChange(n)}
            style={{
              width: '28px', height: '28px', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
              background: n <= value ? (value <= 2 ? '#ffcdd2' : value <= 3 ? '#fff9c4' : '#c8e6c9') : '#f5f5f5',
              color: n <= value ? (value <= 2 ? '#c62828' : value <= 3 ? '#f57f17' : '#2e7d32') : '#bbb',
              fontWeight: 'bold', fontSize: '12px'
            }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  const DurumBadge = ({ durum }) => {
    const colors = {
      'OK': { bg: '#c8e6c9', color: '#2e7d32' },
      'EKSIK': { bg: '#ffcdd2', color: '#c62828' },
      'FAZLA': { bg: '#fff9c4', color: '#f57f17' },
      'HASARLI': { bg: '#e0e0e0', color: '#424242' },
      'ONAYLANDI': { bg: '#c8e6c9', color: '#2e7d32' },
      'BEKLEMEDE': { bg: '#fff9c4', color: '#f57f17' },
      'REDDEDILDI': { bg: '#ffcdd2', color: '#c62828' }
    };
    const s = colors[durum] || { bg: '#e9ecef', color: '#495057' };
    return <span style={{ padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold', background: s.bg, color: s.color }}>{durum}</span>;
  };

  // ─── Aranabilir Malzeme Seçici ───
  const MalzemeSecici = ({ value, onChange, malzemeListesi }) => {
    const [arama, setArama] = useState('');
    const [acik, setAcik] = useState(false);
    const [focusIdx, setFocusIdx] = useState(-1);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Seçili malzemeyi göster
    const secili = malzemeListesi.find(m => m.id === Number(value));

    // Dışarı tıklayınca kapat
    useEffect(() => {
      const handleClick = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
          setAcik(false);
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Filtrelenmiş liste
    const aramaLower = normalizeForSearch(arama);
    const filtrelenmis = aramaLower
      ? malzemeListesi.filter(m =>
          searchMatchPre(m.ad, aramaLower) ||
          (m.kod && searchMatchPre(m.kod, aramaLower))
        )
      : malzemeListesi;

    // Scroll into view
    useEffect(() => {
      if (focusIdx >= 0 && listRef.current) {
        const el = listRef.current.children[focusIdx];
        if (el) el.scrollIntoView({ block: 'nearest' });
      }
    }, [focusIdx]);

    const handleSelect = (m) => {
      onChange(String(m.id));
      setArama('');
      setAcik(false);
      setFocusIdx(-1);
    };

    const handleKeyDown = (e) => {
      if (!acik && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setAcik(true);
        return;
      }
      if (!acik) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx(prev => Math.min(prev + 1, filtrelenmis.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filtrelenmis.length > 0) {
        // Liste açık + sonuç var → vurgulanan (yoksa ilk) seçeneği seç.
        // EnterNavForm advance etmesin diye preventDefault (boş seçim bırakmaz).
        e.preventDefault();
        handleSelect(filtrelenmis[focusIdx >= 0 ? focusIdx : 0]);
      } else if (e.key === 'Escape') {
        setAcik(false);
        setFocusIdx(-1);
      }
    };

    return (
      <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          value={acik ? arama : (secili ? `${secili.ad} (${secili.birim || 'kg'})` : '')}
          placeholder="Malzeme ara..."
          onChange={e => { setArama(e.target.value); setAcik(true); setFocusIdx(-1); }}
          onFocus={() => { setAcik(true); setArama(''); }}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%', padding: '6px 28px 6px 8px', border: `1px solid ${acik ? styles.primary : '#ddd'}`,
            borderRadius: radius.sm, fontSize: '12px', boxSizing: 'border-box',
            background: secili && !acik ? '#f0f7ff' : '#fff'
          }}
        />
        {/* Dropdown oku */}
        <span style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          fontSize: 10, color: colors.textTertiary, pointerEvents: 'none'
        }}>
          {acik ? '▲' : '▼'}
        </span>
        {/* Seçimi temizle */}
        {secili && !acik && (
          <span
            onClick={() => { onChange(''); setArama(''); }}
            style={{
              position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
              fontSize: 12, color: colors.textTertiary, cursor: 'pointer', padding: '0 4px'
            }}
          >✕</span>
        )}

        {/* Dropdown listesi */}
        {acik && (
          <div ref={listRef} style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
            background: colors.surface, border: `1px solid ${colors.border}`, borderTop: 'none',
            borderRadius: '0 0 4px 4px', maxHeight: '220px', overflowY: 'auto',
            boxShadow: shadows.md
          }}>
            {filtrelenmis.length === 0 ? (
              <div style={{ padding: '10px 12px', color: colors.textTertiary, fontSize: '12px', textAlign: 'center' }}>
                Sonuç bulunamadı
              </div>
            ) : (
              filtrelenmis.slice(0, 50).map((m, i) => (
                <div
                  key={m.id}
                  onClick={() => handleSelect(m)}
                  style={{
                    padding: '7px 10px', cursor: 'pointer', fontSize: '12px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: i === focusIdx ? '#e3f2fd' : (Number(value) === m.id ? '#f0f7ff' : 'transparent'),
                    borderBottom: '1px solid #f5f5f5'
                  }}
                  onMouseEnter={() => setFocusIdx(i)}
                >
                  <span style={{ fontWeight: Number(value) === m.id ? 'bold' : 'normal' }}>
                    {aramaLower ? highlightMatch(m.ad, aramaLower) : m.ad}
                  </span>
                  <span style={{ color: colors.textTertiary, fontSize: '11px', marginLeft: 8, whiteSpace: 'nowrap' }}>
                    {m.birim || 'kg'}
                  </span>
                </div>
              ))
            )}
            {filtrelenmis.length > 50 && (
              <div style={{ padding: '6px 10px', color: colors.textTertiary, fontSize: '11px', textAlign: 'center', borderTop: `1px solid ${colors.border}` }}>
                +{filtrelenmis.length - 50} sonuç daha — aramayı daraltın
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Arama eşleşmesini vurgula
  const highlightMatch = (text, query) => {
    const idx = text.toLowerCase().indexOf(query);
    if (idx === -1) return text;
    return (
      <span>
        {text.slice(0, idx)}
        <strong style={{ color: styles.primary }}>{text.slice(idx, idx + query.length)}</strong>
        {text.slice(idx + query.length)}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>📋 Mal Kabul</h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', padding: '0 20px' }}>
        {[
          { id: 'yeni', label: '➕ Yeni Mal Kabul' },
          { id: 'gecmis', label: '📜 Geçmiş Kayıtlar' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
              background: activeTab === tab.id ? 'white' : 'transparent',
              color: activeTab === tab.id ? styles.primary : '#666',
              borderBottom: activeTab === tab.id ? `3px solid ${styles.primary}` : '3px solid transparent',
              marginBottom: '-2px'
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: Yeni Mal Kabul */}
      {activeTab === 'yeni' && (
        <div style={{ padding: '20px', maxWidth: '1100px' }}>
          {result ? (
            /* Sonuç Ekranı */
            <div style={{ background: result.durum === 'ONAYLANDI' ? '#e8f5e9' : '#ffebee', border: `2px solid ${result.durum === 'ONAYLANDI' ? '#4caf50' : '#f44336'}`, borderRadius: radius.lg, padding: '30px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>{result.durum === 'ONAYLANDI' ? '✅' : '❌'}</div>
              <h2 style={{ margin: '0 0 10px', color: result.durum === 'ONAYLANDI' ? '#2e7d32' : '#c62828' }}>
                {result.durum === 'ONAYLANDI' ? 'Mal Kabul Tamamlandı!' : 'Kalite Kontrol RED'}
              </h2>
              <p style={{ color: colors.textSecondary, margin: '0 0 20px' }}>GRN No: <strong>{result.grn_no}</strong></p>

              {result.lot_numaralari && result.lot_numaralari.length > 0 && (
                <div style={{ background: colors.surface, borderRadius: radius.md, padding: '15px', marginTop: '15px', textAlign: 'left' }}>
                  <h3 style={{ margin: '0 0 10px', fontSize: '14px', color: '#333' }}>Oluşan LOT Numaraları:</h3>
                  {result.lot_numaralari.map((lot, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: i % 2 === 0 ? '#f8f9fa' : 'white', borderRadius: radius.sm, marginBottom: '4px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary }}>{lot.lot_no}</span>
                      <span>{lot.malzeme_ad} — {lot.miktar} kg</span>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={resetForm} style={{ marginTop: '20px', padding: '10px 30px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                Yeni Mal Kabul
              </button>
            </div>
          ) : (
            /* Form — Enter ile alan gezme (onSubmit BAĞLI DEĞİL: terminal
               aksiyon = geri-alınamaz "Onayla ve Stoğa Al", Enter ile
               tetiklenmemeli; sadece alan-gezme + kalem satır-ekleme).
               onRowAdd yalnız doğrudan modda anlamlı (data-enter-row orada). */
            <EnterNavForm onRowAdd={mode === 'dogrudan' ? addDogruKalem : undefined}>
              {/* Mod Seçimi */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button onClick={() => { setMode('siparis'); setKalemler([]); }}
                  style={{ padding: '8px 20px', borderRadius: radius.sm, border: `2px solid ${mode === 'siparis' ? styles.primary : '#ddd'}`, background: mode === 'siparis' ? '#e3f2fd' : 'white', color: mode === 'siparis' ? styles.primary : '#666', cursor: 'pointer', fontWeight: 'bold' }}>
                  📦 Siparişten
                </button>
                <button onClick={() => { setMode('dogrudan'); setKalemler([]); setSelectedSiparisId(''); }}
                  style={{ padding: '8px 20px', borderRadius: radius.sm, border: `2px solid ${mode === 'dogrudan' ? styles.primary : '#ddd'}`, background: mode === 'dogrudan' ? '#e3f2fd' : 'white', color: mode === 'dogrudan' ? styles.primary : '#666', cursor: 'pointer', fontWeight: 'bold' }}>
                  ✍️ Doğrudan Giriş
                </button>
              </div>

              {/* Üst Bilgiler */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px', marginBottom: '15px' }}>
                {mode === 'siparis' ? (
                  <div>
                    <label style={{ fontSize: '12px', color: colors.textSecondary }}>Sipariş</label>
                    <select value={selectedSiparisId} onChange={e => handleSiparisSecimi(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                      <option value="">Sipariş seçin...</option>
                      {siparisler.map(s => (
                        <option key={s.id} value={s.id}>{s.siparis_no} - {s.tedarikci_ad || s.tedarikci_unvan || '?'} ({s.durum})</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={{ fontSize: '12px', color: colors.textSecondary }}>Tedarikçi</label>
                    <select value={selectedTedarikciId} onChange={e => setSelectedTedarikciId(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                      <option value="">Tedarikçi seçin...</option>
                      {tedarikciler.map(t => (
                        <option key={t.id} value={t.id}>{t.unvan || t.ad}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: '12px', color: colors.textSecondary }}>Lokasyon</label>
                  <select value={selectedLokasyonId} onChange={e => setSelectedLokasyonId(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                    {lokasyonlar.map(l => (
                      <option key={l.id} value={l.id}>{l.ad}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: colors.textSecondary }}>Teslimat Notu</label>
                  <input value={teslimatNotu} onChange={e => setTeslimatNotu(e.target.value)} placeholder="Opsiyonel not..."
                    style={{ width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Kalemler */}
              <div style={{ background: colors.backgroundAlt, borderRadius: radius.md, padding: '15px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px' }}>Kalemler ({kalemler.length})</h3>
                  {mode === 'dogrudan' && (
                    <button onClick={addDogruKalem} style={{ padding: '6px 14px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}>+ Kalem Ekle</button>
                  )}
                </div>

                {kalemler.length === 0 ? (
                  <p style={{ color: colors.textTertiary, textAlign: 'center', padding: '20px' }}>
                    {mode === 'siparis' ? 'Yukarıdan sipariş seçin' : '+ Kalem Ekle butonuna tıklayın'}
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#e9ecef', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Malzeme</th>
                          {mode === 'dogrudan' && <th style={{ padding: '8px', textAlign: 'left' }}>Alış Birimi</th>}
                          <th style={{ padding: '8px', textAlign: 'right' }}>Sipariş</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Teslim</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Fark</th>
                          <th style={{ padding: '8px', textAlign: 'center' }}>Durum</th>
                          {mode === 'dogrudan' && <th style={{ padding: '8px', textAlign: 'right' }}>Fiyat (₺/kg)</th>}
                          {mode === 'dogrudan' && <th style={{ padding: '8px', width: '40px' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {kalemler.map((k, idx) => {
                          const fark = (Number(k.teslim_miktar) || 0) - (Number(k.siparis_miktar) || 0);
                          return (
                            <tr key={idx} {...(mode === 'dogrudan' ? { 'data-enter-row': '' } : {})} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <td style={{ padding: '6px 8px', minWidth: '200px' }}>
                                {mode === 'dogrudan' ? (
                                  <MalzemeSecici
                                    value={k.malzeme_id}
                                    onChange={(val) => updateKalem(idx, 'malzeme_id', val)}
                                    malzemeListesi={malzemeler}
                                  />
                                ) : (
                                  <span style={{ fontWeight: '500' }}>{k.malzeme_ad}</span>
                                )}
                              </td>
                              {mode === 'dogrudan' && (
                                <td style={{ padding: '6px 8px' }}>
                                  {getGirisBirimiOpts(k).length > 0 ? (
                                    <select value={k.giris_birimi || ''} onChange={e => updateKalem(idx, 'giris_birimi', e.target.value)}
                                      title="Teslim aldığınız birim. Alış birimi seçilirse stok birimine çevrilir; boş bırakılırsa miktar stok biriminde varsayılır."
                                      style={{ width: '120px', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px' }}>
                                      <option value="">{(k.birim || 'stok')} (çevirme yok)</option>
                                      {getGirisBirimiOpts(k).map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                  ) : (
                                    <span style={{ fontSize: '12px', color: colors.textTertiary }}>{k.birim || '-'}</span>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                {mode === 'dogrudan' ? (
                                  <input type="number" value={k.siparis_miktar} onChange={e => updateKalem(idx, 'siparis_miktar', Number(e.target.value))}
                                    style={{ width: '80px', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, textAlign: 'right', fontSize: '12px' }} />
                                ) : (
                                  <span>{k.siparis_miktar} {k.birim}</span>
                                )}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                <input type="number" step="0.01" value={k.teslim_miktar}
                                  onChange={e => updateKalem(idx, 'teslim_miktar', Number(e.target.value))}
                                  style={{ width: '80px', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, textAlign: 'right', fontSize: '12px' }} />
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: fark < 0 ? '#c62828' : fark > 0 ? '#f57f17' : '#2e7d32', fontWeight: 'bold' }}>
                                {fark > 0 ? '+' : ''}{fark.toFixed(1)}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}><DurumBadge durum={k.durum} /></td>
                              {mode === 'dogrudan' && (
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                  <input type="number" step="0.01" data-enter-nav="row-add" value={k.birim_fiyat || ''} onChange={e => updateKalem(idx, 'birim_fiyat', e.target.value)}
                                    style={{ width: '80px', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, textAlign: 'right', fontSize: '12px' }} />
                                </td>
                              )}
                              {mode === 'dogrudan' && (
                                <td style={{ padding: '6px 8px' }}>
                                  <button onClick={() => removeKalem(idx)} style={{ background: '#ffebee', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', color: '#c62828', fontSize: '12px' }}>✕</button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', padding: '10px 8px 0', fontSize: '13px', fontWeight: 'bold' }}>
                      <span>Sipariş: {toplamSiparis.toFixed(1)} kg</span>
                      <span>Teslim: {toplamTeslim.toFixed(1)} kg</span>
                      <span style={{ color: toplamTeslim - toplamSiparis < 0 ? '#c62828' : '#2e7d32' }}>
                        Fark: {(toplamTeslim - toplamSiparis).toFixed(1)} kg
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Kalite Kontrol */}
              {kalemler.length > 0 && (
                <div style={{ background: '#fff8e1', borderRadius: radius.md, padding: '15px', marginBottom: '15px', border: '1px solid #ffe082' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>🔬 Kalite Kontrol</h3>

                  <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <SkorInput label="Renk" icon="🎨" value={renkSkoru} onChange={setRenkSkoru} />
                    <SkorInput label="Koku" icon="👃" value={kokuSkoru} onChange={setKokuSkoru} />
                    <SkorInput label="Doku" icon="🤚" value={dokuSkoru} onChange={setDokuSkoru} />
                  </div>

                  <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'flex-end' }}>
                    <div style={{ minWidth: '120px' }}>
                      <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>🌡️ Sıcaklık (°C)</label>
                      <input type="number" step="0.1" value={sicaklikC} onChange={e => setSicaklikC(e.target.value)}
                        placeholder="Ör: 2.5"
                        style={{ width: '100px', padding: '6px', border: `1px solid ${sicaklikC && Number(sicaklikC) > 4 ? '#c62828' : '#ddd'}`, borderRadius: radius.sm, fontSize: '13px' }} />
                      {sicaklikC && Number(sicaklikC) > 4 && <div style={{ color: '#c62828', fontSize: '11px', marginTop: '2px' }}>⚠️ 4°C üstü!</div>}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={sicaklikOk} onChange={e => setSicaklikOk(e.target.checked)} /> Sıcaklık OK
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={yagOraniOk} onChange={e => setYagOraniOk(e.target.checked)} /> 🥩 Yağ Oranı OK
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={ambalajOk} onChange={e => setAmbalajOk(e.target.checked)} /> 📦 Ambalaj OK
                    </label>
                  </div>

                  <input value={kaliteNot} onChange={e => setKaliteNot(e.target.value)} placeholder="Kontrol notu (opsiyonel)"
                    style={{ width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px' }} />

                  <div style={{ padding: '8px 12px', borderRadius: radius.sm, background: kaliteSonuc === 'ONAYLANDI' ? '#c8e6c9' : '#ffcdd2', color: kaliteSonuc === 'ONAYLANDI' ? '#2e7d32' : '#c62828', fontWeight: 'bold', fontSize: '13px' }}>
                    Sonuç Önizleme: {kaliteSonuc === 'ONAYLANDI' ? '✅ ONAYLANACAK' : '❌ REDDEDİLECEK'}
                  </div>
                </div>
              )}

              {/* Submit */}
              {kalemler.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={resetForm} style={{ padding: '10px 24px', background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: '14px' }}>
                    İptal
                  </button>
                  <button onClick={handleSubmit} disabled={submitting || !canManage}
                    style={{ padding: '10px 30px', background: kaliteSonuc === 'ONAYLANDI' ? '#4caf50' : '#f44336', color: 'white', border: 'none', borderRadius: radius.sm, cursor: submitting ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '14px', opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? 'İşleniyor...' : (kaliteSonuc === 'ONAYLANDI' ? '✅ Onayla ve Stoğa Al' : '❌ Kaydet (RED)')}
                  </button>
                </div>
              )}
            </EnterNavForm>
          )}
        </div>
      )}

      {/* TAB 2: Geçmiş Kayıtlar */}
      {activeTab === 'gecmis' && (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <select value={gecmisFiltre} onChange={e => { setGecmisFiltre(e.target.value); }}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
              <option value="">Tüm Durumlar</option>
              <option value="ONAYLANDI">Onaylı</option>
              <option value="BEKLEMEDE">Beklemede</option>
              <option value="REDDEDILDI">Reddedildi</option>
            </select>
            <button onClick={loadGecmisGrnler} style={{ padding: '8px 16px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px' }}>
              Yenile
            </button>
          </div>

          {gecmisLoading ? (
            <p style={{ textAlign: 'center', color: colors.textTertiary, padding: '40px' }}>Yükleniyor...</p>
          ) : gecmisGrnler.length === 0 ? (
            <p style={{ textAlign: 'center', color: colors.textTertiary, padding: '40px' }}>Kayıt bulunamadı</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '7px', textAlign: 'left' }}>GRN No</th>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Tedarikçi</th>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Teslim (kg)</th>
                  <th style={{ padding: '7px', textAlign: 'center' }}>Kalem</th>
                  <th style={{ padding: '7px', textAlign: 'center' }}>Kalite</th>
                  <th style={{ padding: '7px', textAlign: 'center' }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {gecmisGrnler.map(g => (
                  <React.Fragment key={g.id}>
                    <tr style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer' }}
                      onClick={() => { if (expandedGrn === g.id) { setExpandedGrn(null); setExpandedDetail(null); } else { setExpandedGrn(g.id); loadGrnDetail(g.id); } }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary }}>{g.grn_no}</td>
                      <td style={{ padding: '10px' }}>{g.tedarikci_ad || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', color: colors.textSecondary }}>{g.teslim_tarihi ? new Date(g.teslim_tarihi).toLocaleDateString('tr-TR') : '-'}</td>
                      <td style={{ padding: '7px', textAlign: 'right' }}>{g.teslim_toplam_kg ? g.teslim_toplam_kg.toFixed(1) : '-'}</td>
                      <td style={{ padding: '7px', textAlign: 'center' }}>{g.kalem_sayisi || '-'}</td>
                      <td style={{ padding: '7px', textAlign: 'center' }}>{g.kalite_durum ? <DurumBadge durum={g.kalite_durum} /> : '-'}</td>
                      <td style={{ padding: '7px', textAlign: 'center' }}><DurumBadge durum={g.durum} /></td>
                    </tr>
                    {expandedGrn === g.id && expandedDetail && (
                      <tr>
                        <td colSpan={7} style={{ padding: '10px 20px', background: '#f0f4f8' }}>
                          <div style={{ fontSize: '12px' }}>
                            <strong>Kalemler:</strong>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ background: '#e9ecef' }}>
                                  <th style={{ padding: '6px', textAlign: 'left' }}>Malzeme</th>
                                  <th style={{ padding: '6px', textAlign: 'right' }}>Teslim</th>
                                  <th style={{ padding: '6px', textAlign: 'center' }}>Durum</th>
                                  <th style={{ padding: '6px', textAlign: 'left' }}>LOT No</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(expandedDetail.kalemler || []).map((k, i) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                    <td style={{ padding: '6px' }}>{k.malzeme_ad}</td>
                                    <td style={{ padding: '6px', textAlign: 'right' }}>{k.teslim_miktar} kg</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}><DurumBadge durum={k.durum} /></td>
                                    <td style={{ padding: '6px', fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary }}>{k.lot_no || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default BirlesikMalKabulPage;
